<template>
  <div class="resident-home">
    <div v-if="loading" class="home-skeleton" aria-label="Carregando informações">
      <section class="skeleton-hero">
        <div>
          <span class="skeleton-line short"></span>
          <span class="skeleton-line title"></span>
          <span class="skeleton-line medium"></span>
        </div>
        <span class="skeleton-avatar"></span>
      </section>

      <section class="skeleton-card skeleton-status">
        <div>
          <span class="skeleton-line short"></span>
          <span class="skeleton-line heading"></span>
          <span class="skeleton-line small"></span>
        </div>
        <span class="skeleton-button"></span>
      </section>

      <section class="skeleton-card skeleton-statement">
        <div class="skeleton-statement-head">
          <div>
            <span class="skeleton-line short"></span>
            <span class="skeleton-line heading"></span>
          </div>
          <span class="skeleton-line value"></span>
        </div>
        <div class="skeleton-metric-grid">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class="skeleton-chart"></div>
      </section>

      <section class="skeleton-shortcuts">
        <span v-for="item in 8" :key="item"></span>
      </section>

      <section class="skeleton-card skeleton-panel">
        <span class="skeleton-line heading"></span>
        <span class="skeleton-line medium"></span>
        <span class="skeleton-row"></span>
        <span class="skeleton-row"></span>
      </section>
    </div>

    <template v-else>
    <section class="hero-panel">
      <div>
        <span>Olá, {{ firstName }}</span>
        <h1>{{ associationName }}</h1>
        <p>{{ homeSummary }}</p>
      </div>
      <img src="/img/boarua-icon-concept-v1.png" alt="" />
    </section>

    <section class="home-action-strip" aria-label="Ações principais">
      <router-link v-for="item in priorityActions" :key="item.title" :to="item.route" class="action-card">
        <span class="action-icon">
          <v-icon>{{ item.icon }}</v-icon>
        </span>
        <span class="action-copy">
          <strong>{{ item.title }}</strong>
          <small>{{ item.caption }}</small>
        </span>
      </router-link>
    </section>

    <section class="status-card">
      <div>
        <span>Financeiro</span>
        <strong>{{ financeMain }}</strong>
        <small>{{ financeCaption }}</small>
      </div>
      <router-link to="/financeiro">
        Ver
        <v-icon small>mdi-arrow-right</v-icon>
      </router-link>
    </section>

    <section class="promotions-panel">
      <div class="panel-header compact">
        <div>
          <h2>Comércios e promoções</h2>
          <p>{{ promotionsCaption }}</p>
        </div>
        <router-link to="/comercio-local">
          Ver todos
          <v-icon small>mdi-arrow-right</v-icon>
        </router-link>
      </div>

      <div v-if="featuredBusinesses.length" class="promotion-rail">
        <router-link
          v-for="item in featuredBusinesses"
          :key="item.id || item.name"
          to="/comercio-local"
          class="promotion-card"
        >
          <span class="promotion-category">{{ categoryLabel(item.category) }}</span>
          <strong>{{ item.name }}</strong>
          <p>{{ promotionText(item) }}</p>
          <small v-if="item.coupon_code">
            <v-icon size="14">mdi-ticket-percent-outline</v-icon>
            Cupom {{ item.coupon_code }}
          </small>
        </router-link>
      </div>

      <router-link v-else to="/comercio-local" class="promotion-empty">
        <v-icon>mdi-storefront-outline</v-icon>
        <span>
          <strong>Explore os comércios do local</strong>
          <small>Ofertas e cupons aparecerão aqui quando forem publicados.</small>
        </span>
      </router-link>
    </section>

    <section class="monthly-statement-card">
      <div class="statement-header">
        <div>
          <span>Demonstrativo mensal</span>
          <h2>{{ currentMonthLabel }}</h2>
        </div>
        <strong :class="{ negative: monthlyStatement.finalBalance < 0 }">{{ money(monthlyStatement.finalBalance) }}</strong>
      </div>

      <div class="statement-values">
        <article>
          <span>Receitas</span>
          <strong>{{ money(monthlyStatement.revenueAmount) }}</strong>
          <small>Entradas confirmadas</small>
        </article>
        <article>
          <span>Despesas</span>
          <strong>{{ money(monthlyStatement.expenseAmount) }}</strong>
          <small>Saídas publicadas</small>
        </article>
        <article>
          <span>Saldo do mês</span>
          <strong :class="{ negative: monthlyStatement.finalBalance < 0 }">{{ money(monthlyStatement.finalBalance) }}</strong>
          <small>Receitas menos despesas</small>
        </article>
      </div>

      <div class="statement-history">
        <div class="statement-history-heading">
          <strong>Evolução mensal</strong>
          <span>{{ statementHistoryCaption }}</span>
        </div>

        <div class="statement-history-chart" aria-label="Receitas, despesas e saldo por mês">
          <div v-for="month in monthlyStatementHistory" :key="month.referenceMonth" class="statement-month">
            <div class="statement-month-bars">
              <span class="month-bar revenue" :style="{ height: month.revenueHeight }"></span>
              <span class="month-bar expense" :style="{ height: month.expenseHeight }"></span>
              <span :class="['month-bar', 'balance', { negative: month.finalBalance < 0 }]" :style="{ height: month.balanceHeight }"></span>
            </div>
            <strong>{{ month.shortLabel }}</strong>
            <small :class="{ negative: month.finalBalance < 0 }">{{ money(month.finalBalance) }}</small>
          </div>
        </div>

        <div class="statement-legend">
          <span><i class="revenue"></i>Receitas</span>
          <span><i class="expense"></i>Despesas</span>
          <span><i class="balance"></i>Saldo</span>
        </div>
      </div>
    </section>

    <section class="quick-row">
      <router-link v-for="item in primaryShortcuts" :key="item.title" :to="item.route" class="quick-pill">
        <v-icon>{{ item.icon }}</v-icon>
        <span>{{ item.title }}</span>
      </router-link>
    </section>

    <section class="resident-panel">
      <div class="panel-header">
        <div>
          <h2>Próximos passos</h2>
          <p>{{ loading ? "Atualizando informações..." : "Acompanhe o que precisa de atenção" }}</p>
        </div>
      </div>
      <div v-if="!todos.length && !loading" class="todo-item empty-todo">
        <v-icon color="primary">mdi-check-circle-outline</v-icon>
        <div>
          <strong>Nada pendente agora</strong>
          <span>Quando houver boletos, assembleias ou comunicados, eles aparecerão aqui.</span>
        </div>
      </div>
      <div class="todo-item" v-for="item in todos" :key="item.title">
        <v-icon :color="item.color">{{ item.icon }}</v-icon>
        <div>
          <strong>{{ item.title }}</strong>
          <span>{{ item.description }}</span>
        </div>
      </div>
    </section>

    <section class="mini-grid">
      <router-link to="/demandas" class="mini-card">
        <v-icon color="primary">mdi-clipboard-text-outline</v-icon>
        <span>Chamados</span>
        <strong>{{ demandsCaption }}</strong>
      </router-link>
      <router-link to="/assembleias" class="mini-card">
        <v-icon color="primary">mdi-calendar-check-outline</v-icon>
        <span>Assembleias</span>
        <strong>{{ assembliesCaption }}</strong>
      </router-link>
    </section>
    </template>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaResidentHome",
  data() {
    return {
      loading: false,
      demands: [],
      charges: [],
      statementCharges: [],
      assemblies: [],
      posts: [],
      financialEntries: [],
      businesses: [],
    };
  },
  computed: {
    profile() {
      return JSON.parse(localStorage.getItem("associationProfile") || "{}");
    },
    firstName() {
      const raw = localStorage.getItem("user");
      const user = raw ? JSON.parse(raw) : {};
      const name = user.name || (user.data && user.data.name) || "morador";
      return String(name).split(" ")[0];
    },
    associationName() {
      return this.profile.association ? this.profile.association.name : "BOARUA";
    },
    homeSummary() {
      if (this.nextPendingCharge) return "Você tem uma mensalidade para acompanhar.";
      if (this.nextAssembly) return "Há uma assembleia prevista no seu local.";
      if (this.posts.length) return "Confira os comunicados recentes do local.";
      return "Tudo certo por aqui. Acompanhe seu local em um só lugar.";
    },
    shortcuts() {
      return [
        {
          title: "Segurança",
          icon: "mdi-shield-alert-outline",
          route: "/seguranca",
        },
        {
          title: "Encomendas",
          icon: "mdi-package-variant-closed",
          route: "/encomendas",
        },
        {
          title: "Chamados",
          icon: "mdi-clipboard-text-outline",
          route: "/demandas",
        },
        {
          title: "Ofertas",
          icon: "mdi-storefront-outline",
          route: "/comercio-local",
        },
        {
          title: "Votações",
          icon: "mdi-poll",
          route: "/votacoes",
        },
        {
          title: "Relatórios",
          icon: "mdi-file-chart-outline",
          route: "/relatorios",
        },
        {
          title: "Assembleias",
          icon: "mdi-calendar-check-outline",
          route: "/assembleias",
        },
        {
          title: "Feed",
          icon: "mdi-newspaper-variant-outline",
          route: "/feed",
        },
      ];
    },
    priorityActions() {
      return [
        {
          title: "Enviar alerta",
          caption: "Segurança do local",
          icon: "mdi-shield-alert-outline",
          route: "/seguranca",
        },
        {
          title: "Liberar visitante",
          caption: "Portaria",
          icon: "mdi-gate",
          route: "/portaria",
        },
        {
          title: "Abrir chamado",
          caption: "Solicitações",
          icon: "mdi-clipboard-text-outline",
          route: "/demandas",
        },
      ];
    },
    primaryShortcuts() {
      return this.shortcuts.filter((item) => !["Segurança", "Chamados"].includes(item.title)).slice(0, 6);
    },
    featuredBusinesses() {
      const ordered = [...this.businesses].sort((a, b) => {
        const featured = Number(Boolean(b.is_featured)) - Number(Boolean(a.is_featured));
        if (featured) return featured;
        return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
      });

      return ordered.slice(0, 5);
    },
    promotionsCaption() {
      if (!this.businesses.length) return "Benefícios e serviços próximos de você";
      const count = this.businesses.length;
      return count === 1 ? "1 comércio disponível para moradores" : `${count} comércios disponíveis para moradores`;
    },
    financeMain() {
      if (this.nextPendingCharge) return this.money(this.nextPendingCharge.amount);
      if (!this.charges.length) return "Sem faturas";
      return "Em dia";
    },
    demandsCaption() {
      const open = this.demands.filter((item) => !["closed", "done", "cancelled"].includes(String(item.status || "").toLowerCase())).length;
      if (!this.demands.length) return "Nenhuma demanda";
      if (!open) return "Tudo resolvido";
      return `${open} em aberto`;
    },
    financeCaption() {
      if (!this.charges.length) return "Nenhum boleto";

      const pending = this.nextPendingCharge;
      if (pending) {
        return `${this.money(pending.amount)} vence ${this.shortDate(pending.due_date)}`;
      }

      const paid = this.charges.filter((item) => String(item.status || "").toLowerCase() === "paid").length;
      return `${paid} de ${this.charges.length} pagos`;
    },
    assembliesCaption() {
      if (!this.assemblies.length) return "Nenhuma agenda";
      if (!this.nextAssembly) return "Sem próximas datas";
      return this.shortDateTime(this.nextAssembly.starts_at);
    },
    feedCaption() {
      if (!this.posts.length) return "Nenhum comunicado";
      return this.posts[0].title || "Último comunicado";
    },
    currentReferenceMonth() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    },
    currentMonthLabel() {
      const now = new Date();
      return this.formatMonthLabel(this.currentReferenceMonth);
    },
    monthlyStatement() {
      return this.monthlyStatementFor(this.currentReferenceMonth);
    },
    monthlyStatementHistory() {
      const months = this.statementReferenceMonths();
      const rows = months.map((referenceMonth) => this.monthlyStatementFor(referenceMonth));
      const max = Math.max(
        1,
        ...rows.flatMap((item) => [
          Math.abs(Number(item.revenueAmount || 0)),
          Math.abs(Number(item.expenseAmount || 0)),
          Math.abs(Number(item.finalBalance || 0)),
        ])
      );

      return rows.map((item) => ({
        ...item,
        label: this.formatMonthLabel(item.referenceMonth),
        shortLabel: this.formatShortMonthLabel(item.referenceMonth),
        revenueHeight: this.statementBarHeight(item.revenueAmount, max),
        expenseHeight: this.statementBarHeight(item.expenseAmount, max),
        balanceHeight: this.statementBarHeight(item.finalBalance, max),
      }));
    },
    statementHistoryCaption() {
      const count = this.monthlyStatementHistory.length;
      return count === 1 ? "1 mês apurado" : `${count} meses apurados`;
    },
    nextPendingCharge() {
      return this.charges
        .filter((item) => ["pending", "overdue"].includes(String(item.status || "").toLowerCase()))
        .sort((a, b) => this.dateValue(a.due_date) - this.dateValue(b.due_date))[0];
    },
    nextAssembly() {
      const now = new Date();
      return this.assemblies
        .filter((item) => !item.starts_at || new Date(item.starts_at) >= now)
        .sort((a, b) => this.dateValue(a.starts_at) - this.dateValue(b.starts_at))[0];
    },
    todos() {
      const items = [];

      if (this.nextPendingCharge) {
        items.push({
          title: this.nextPendingCharge.status === "overdue" ? "Boleto em atraso" : "Boleto disponível",
          description: `${this.money(this.nextPendingCharge.amount)} ${this.dueText(this.nextPendingCharge.due_date)}`,
          icon: "mdi-barcode",
          color: "primary",
        });
      }

      if (this.nextAssembly) {
        items.push({
          title: this.nextAssembly.title || "Assembleia agendada",
          description: `${this.shortDateTime(this.nextAssembly.starts_at)}${this.nextAssembly.location_name ? ` - ${this.nextAssembly.location_name}` : ""}`,
          icon: "mdi-calendar-check-outline",
          color: "blue",
        });
      }

      const urgentDemand = this.demands.find((item) => ["urgent", "high"].includes(String(item.priority || "").toLowerCase()));
      if (urgentDemand) {
        items.push({
          title: urgentDemand.title || "Demanda prioritária",
          description: urgentDemand.category || urgentDemand.status || "Acompanhe a solicitação",
          icon: "mdi-alert-circle-outline",
          color: "orange",
        });
      }

      const latestPost = this.posts[0];
      if (latestPost) {
        items.push({
          title: latestPost.title || "Novo comunicado",
          description: this.truncate(latestPost.content || "Publicado pela gestão"),
          icon: "mdi-newspaper-variant-outline",
          color: "primary",
        });
      }

      return items.slice(0, 4);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.profile.tenant_id) return;

      this.loading = true;
      try {
        const baseParams = { tenant_id: this.profile.tenant_id, limit: 20 };
        const memberParams = this.profile.id
          ? { ...baseParams, search: JSON.stringify({ member_id: this.profile.id }) }
          : baseParams;

        const [demands, charges, statementCharges, assemblies, posts, financialEntries, businesses] = await Promise.allSettled([
          axios.get("/demands", { params: baseParams }),
          axios.get("/monthly-charges", { params: memberParams }),
          axios.get("/monthly-charges", { params: { ...baseParams, limit: 200 } }),
          axios.get("/assemblies", { params: baseParams }),
          this.loadFeedPosts(baseParams),
          this.loadFinancialEntries(baseParams),
          this.loadLocalBusinesses(baseParams),
        ]);

        this.demands = this.extractItems(demands);
        this.charges = this.extractItems(charges);
        this.statementCharges = this.extractItems(statementCharges);
        this.assemblies = this.extractItems(assemblies);
        this.posts = this.extractItems(posts);
        this.financialEntries = this.extractItems(financialEntries);
        this.businesses = this.extractItems(businesses);
      } finally {
        this.loading = false;
      }
    },
    async loadFeedPosts(baseParams) {
      const postTypes = ["announcement", "improvement", "work", "accountability"];
      const responses = await Promise.all(
        postTypes.map((postType) => axios.get("/feed-posts", { params: { ...baseParams, post_type: postType } }))
      );

      return {
        data: {
          content: responses
            .flatMap((response) => response.data.content.data || response.data.content || [])
            .filter((item) => postTypes.includes(item.post_type)),
        },
      };
    },
    async loadFinancialEntries(baseParams) {
      const postTypes = ["revenue", "expense"];
      const responses = await Promise.all(
        postTypes.map((postType) => axios.get("/feed-posts", { params: { ...baseParams, post_type: postType, limit: 200 } }))
      );

      return {
        data: {
          content: responses
            .flatMap((response) => response.data.content.data || response.data.content || [])
            .filter((item) => postTypes.includes(item.post_type)),
        },
      };
    },
    loadLocalBusinesses(baseParams) {
      return axios.get("/local-businesses", {
        params: { ...baseParams, limit: 8, search: JSON.stringify({ status: "active" }) },
      });
    },
    extractItems(result) {
      if (result.status !== "fulfilled") return [];

      const content = result.value.data && result.value.data.content;
      if (Array.isArray(content)) return content;
      if (content && Array.isArray(content.data)) return content.data;

      return [];
    },
    categoryLabel(value) {
      const map = {
        pharmacy: "Farmácia",
        food: "Alimentação",
        market: "Mercado",
        services: "Serviços",
        health: "Saúde",
        beauty: "Beleza",
        pet: "Pet",
        other: "Comércio",
      };

      return map[value] || value || "Comércio";
    },
    promotionText(item) {
      return item.promotion_title || item.promotion_description || item.description || "Benefício disponível para moradores.";
    },
    dateValue(value) {
      if (!value) return Number.MAX_SAFE_INTEGER;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
    },
    shortDate(value) {
      if (!value) return "sem data";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "sem data";
      return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    },
    shortDateTime(value) {
      if (!value) return "Data a definir";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Data a definir";
      return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    },
    dueText(value) {
      if (!value) return "sem vencimento";

      const today = new Date();
      const due = new Date(value);
      if (Number.isNaN(due.getTime())) return "sem vencimento";

      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);

      const days = Math.round((due.getTime() - today.getTime()) / 86400000);
      if (days < 0) return `vencido há ${Math.abs(days)} dia${Math.abs(days) === 1 ? "" : "s"}`;
      if (days === 0) return "vence hoje";
      return `vence em ${days} dia${days === 1 ? "" : "s"}`;
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    statementReferenceMonths() {
      const months = new Set([this.currentReferenceMonth]);
      [...this.statementCharges, ...this.financialEntries].forEach((item) => {
        const referenceMonth = this.itemReferenceMonth(item);
        if (referenceMonth) months.add(referenceMonth);
      });

      return Array.from(months)
        .sort();
    },
    monthlyStatementFor(referenceMonth) {
      const paidCharges = this.statementCharges.filter((item) => {
        const status = String(item.status || "").toLowerCase();
        return status === "paid" && this.itemReferenceMonth(item) === referenceMonth;
      });
      const chargeRevenue = this.sumAmount(paidCharges);
      const revenueEntries = this.financialEntries.filter((item) => item.post_type === "revenue" && this.itemReferenceMonth(item) === referenceMonth);
      const expenseEntries = this.financialEntries.filter((item) => item.post_type === "expense" && this.itemReferenceMonth(item) === referenceMonth);
      const revenueAmount = chargeRevenue + this.sumAmount(revenueEntries);
      const expenseAmount = this.sumAmount(expenseEntries);

      return {
        referenceMonth,
        revenueAmount,
        expenseAmount,
        finalBalance: revenueAmount - expenseAmount,
      };
    },
    statementBarHeight(value, max) {
      const amount = Math.abs(Number(value || 0));
      if (!amount) return "8%";
      return `${Math.max(12, Math.round((amount / Math.max(1, max)) * 100))}%`;
    },
    formatMonthLabel(referenceMonth) {
      const date = this.referenceMonthDate(referenceMonth);
      if (!date) return "Mês atual";
      const month = date.toLocaleDateString("pt-BR", { month: "long" });
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} de ${date.getFullYear()}`;
    },
    formatShortMonthLabel(referenceMonth) {
      const date = this.referenceMonthDate(referenceMonth);
      if (!date) return "--";
      return date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
    },
    referenceMonthDate(referenceMonth) {
      const match = String(referenceMonth || "").match(/^(\d{4})-(\d{2})$/);
      if (!match) return null;
      return new Date(Number(match[1]), Number(match[2]) - 1, 1);
    },
    itemReferenceMonth(item = {}) {
      if (item.reference_month) return String(item.reference_month).slice(0, 7);
      const value = item.paid_at || item.published_at || item.created_at || item.updated_at;
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    },
    sumAmount(items = []) {
      return items.reduce((total, item) => total + Number(item.amount || 0), 0);
    },
    truncate(value) {
      const text = String(value || "").replace(/<[^>]+>/g, "").trim();
      return text.length > 64 ? `${text.slice(0, 61)}...` : text;
    },
  },
};
</script>

<style scoped lang="scss">
.resident-home {
  min-height: 100%;
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: 18px 20px 118px;
  color: #111827;
}

.hero-panel {
  min-height: 150px;
  margin: 0 0 14px;
  padding: 26px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  color: #ffffff;
  background:
    radial-gradient(circle at 80% 12%, rgba(255, 255, 255, 0.18), transparent 30%),
    linear-gradient(135deg, #057a3d, #003f24);
  border-radius: 22px;
  box-shadow: 0 18px 44px rgba(0, 63, 36, 0.16);

  span {
    display: block;
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  h1 {
    margin: 0;
    font-size: 30px;
    line-height: 1.15;
  }

  p {
    max-width: 430px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 15px;
    line-height: 1.45;
  }

  img {
    width: 54px;
    height: 54px;
    flex: 0 0 54px;
    object-fit: cover;
    border-radius: 16px;
    background: #ffffff;
  }
}

.home-skeleton {
  display: grid;
  gap: 14px;
}

.skeleton-hero,
.skeleton-card,
.skeleton-shortcuts span {
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}

.skeleton-hero {
  min-height: 150px;
  padding: 26px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  background:
    radial-gradient(circle at 80% 12%, rgba(255, 255, 255, 0.18), transparent 30%),
    linear-gradient(135deg, #057a3d, #003f24);
  border: 0;
  border-radius: 22px;

  > div {
    width: min(70%, 460px);
    display: grid;
    gap: 12px;
  }
}

.skeleton-card {
  padding: 18px;
}

.skeleton-status {
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  > div {
    flex: 1;
    display: grid;
    gap: 10px;
  }
}

.skeleton-statement {
  display: grid;
  gap: 14px;
}

.skeleton-statement-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  > div {
    width: min(48%, 260px);
    display: grid;
    gap: 10px;
  }
}

.skeleton-metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  span {
    min-height: 84px;
    border-radius: 12px;
  }
}

.skeleton-chart {
  min-height: 178px;
  border-radius: 14px;
}

.skeleton-shortcuts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  span {
    min-height: 78px;
  }
}

.skeleton-panel {
  display: grid;
  gap: 10px;
}

.skeleton-row {
  min-height: 58px;
  border-radius: 12px;
}

.skeleton-line,
.skeleton-avatar,
.skeleton-button,
.skeleton-metric-grid span,
.skeleton-chart,
.skeleton-row,
.skeleton-shortcuts span {
  position: relative;
  overflow: hidden;
  background: #eef4ef;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
    animation: skeleton-shimmer 1.35s infinite;
  }
}

.skeleton-hero {
  .skeleton-line,
  .skeleton-avatar {
    background: rgba(255, 255, 255, 0.22);
  }
}

.skeleton-line {
  width: 100%;
  height: 14px;
  display: block;
  border-radius: 999px;

  &.short {
    width: 110px;
  }

  &.small {
    width: 150px;
  }

  &.medium {
    width: min(100%, 360px);
  }

  &.title {
    width: min(100%, 420px);
    height: 34px;
  }

  &.heading {
    width: min(100%, 220px);
    height: 24px;
  }

  &.value {
    width: 140px;
    height: 24px;
  }
}

.skeleton-avatar {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  border-radius: 16px;
}

.skeleton-button {
  width: 76px;
  height: 40px;
  flex: 0 0 76px;
  border-radius: 12px;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.status-card {
  min-height: 112px;
  margin: 0 0 14px;
  padding: 18px 20px;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);

  span,
  small {
    display: block;
    color: #6d7770;
  }

  span {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin: 5px 0 3px;
    color: #111827;
    font-size: 26px;
    line-height: 1.05;
  }

  small {
    font-size: 13px;
  }

  a {
    min-width: 76px;
    height: 40px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #ffffff;
    background: #111827;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 850;
    text-decoration: none;

    .v-icon {
      color: #ffffff;
    }
  }
}

.home-action-strip {
  margin: -6px 0 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.action-card {
  min-height: 86px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}

.action-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #057a3d;
  background: #edf8f1;
  border-radius: 13px;

  .v-icon {
    color: #057a3d;
  }
}

.action-copy {
  min-width: 0;
  display: grid;
  gap: 3px;

  strong,
  small {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 14px;
    line-height: 1.15;
  }

  small {
    color: #66736b;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.promotions-panel {
  margin: 0 0 14px;
  padding: 0 0 14px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}

.panel-header.compact {
  padding: 16px 16px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  h2 {
    font-size: 19px;
  }

  a {
    min-height: 32px;
    padding: 0 4px 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: #057a3d;
    font-size: 12px;
    font-weight: 850;
    text-decoration: none;
    white-space: nowrap;

    .v-icon {
      color: #057a3d;
    }
  }
}

.promotion-rail {
  display: grid;
  grid-auto-columns: minmax(218px, 0.72fr);
  grid-auto-flow: column;
  gap: 10px;
  overflow-x: auto;
  padding: 0 16px 2px;
  scroll-snap-type: x proximity;
}

.promotion-card,
.promotion-empty {
  color: #111827;
  background:
    radial-gradient(circle at 92% 0%, rgba(5, 122, 61, 0.12), transparent 34%),
    #f8fbf9;
  border: 1px solid #e1e8e2;
  border-radius: 15px;
  text-decoration: none;
}

.promotion-card {
  min-height: 132px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  scroll-snap-align: start;

  strong {
    display: block;
    font-size: 16px;
    line-height: 1.15;
  }

  p {
    min-height: 34px;
    margin: 0;
    color: #66736b;
    font-size: 12px;
    line-height: 1.35;
  }

  small {
    width: fit-content;
    max-width: 100%;
    padding: 5px 8px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #057a3d;
    background: #e7f6ed;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 850;

    .v-icon {
      color: #057a3d;
    }
  }
}

.promotion-category {
  width: fit-content;
  max-width: 100%;
  padding: 4px 8px;
  color: #057a3d;
  background: #e7f6ed;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 850;
  text-transform: uppercase;
}

.promotion-empty {
  margin: 0 16px;
  min-height: 92px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  .v-icon {
    color: #057a3d;
    font-size: 28px;
  }

  span,
  strong,
  small {
    display: block;
  }

  strong {
    font-size: 15px;
  }

  small {
    margin-top: 3px;
    color: #66736b;
    font-size: 12px;
    line-height: 1.25;
  }
}

.monthly-statement-card {
  margin: 0 0 14px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}

.statement-header {
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  span {
    display: block;
    color: #057a3d;
    font-size: 11px;
    font-weight: 850;
    text-transform: uppercase;
  }

  h2 {
    margin: 4px 0 0;
    color: #111827;
    font-size: 19px;
    line-height: 1.15;
    text-transform: capitalize;
  }

  > strong {
    color: #057a3d;
    font-size: 22px;
    line-height: 1.1;
    white-space: nowrap;

    &.negative {
      color: #b91c1c;
    }
  }
}

.statement-values {
  margin-bottom: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  article {
    padding: 12px;
    background: #f8fbf9;
    border: 1px solid #edf1ec;
    border-radius: 12px;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
    font-weight: 750;
  }

  strong {
    margin-top: 4px;
    color: #111827;
    font-size: 16px;

    &.negative {
      color: #b91c1c;
    }
  }

  small {
    display: block;
    margin-top: 3px;
    color: #66736b;
    font-size: 11px;
    line-height: 1.25;
  }
}

.statement-history {
  padding: 12px;
  background: #f8fbf9;
  border: 1px solid #edf1ec;
  border-radius: 14px;
}

.statement-history-heading {
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 750;
    white-space: nowrap;
  }
}

.statement-history-chart {
  min-height: 142px;
  display: grid;
  grid-auto-columns: minmax(70px, 1fr);
  grid-auto-flow: column;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.statement-month {
  min-width: 70px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #10223f;
  text-align: center;

  strong {
    font-size: 11px;
    font-weight: 850;
    text-transform: capitalize;
  }

  small {
    color: #66736b;
    font-size: 10px;
    font-weight: 800;
    white-space: nowrap;

    &.negative {
      color: #b91c1c;
    }
  }
}

.statement-month-bars {
  height: 92px;
  padding: 8px 6px 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 5px;
  background: #ffffff;
  border: 1px solid #edf1ec;
  border-radius: 12px;
}

.month-bar {
  width: 13px;
  min-height: 8px;
  border-radius: 8px 8px 0 0;

  &.revenue {
    background: linear-gradient(180deg, #057a3d, #02351f);
  }

  &.expense {
    background: linear-gradient(180deg, #e53935, #ffb4b4);
  }

  &.balance {
    background: linear-gradient(180deg, #10223f, #6b7280);

    &.negative {
      background: linear-gradient(180deg, #b91c1c, #fca5a5);
    }
  }
}

.statement-legend {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #66736b;
    font-size: 11px;
    font-weight: 750;
  }

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &.revenue {
      background: #057a3d;
    }

    &.expense {
      background: #e53935;
    }

    &.balance {
      background: #10223f;
    }
  }
}

.quick-row {
  margin: 0 0 14px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.quick-pill {
  min-height: 78px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  text-decoration: none;

  .v-icon {
    color: #057a3d;
    font-size: 24px;
  }
}

.resident-panel {
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
}

.resident-panel {
  margin: 0 0 14px;
  padding: 0;
  overflow: hidden;
}

.panel-header {
  padding: 18px 18px 7px;
  margin-bottom: 0;

  h2 {
    margin: 0;
    font-size: 21px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.todo-item {
  min-height: 66px;
  padding: 13px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #edf1ec;

  div {
    min-width: 0;
  }

  strong,
  span {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.mini-grid {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.mini-card {
  min-height: 104px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
  text-decoration: none;

  span {
    color: #6d7770;
    font-size: 12px;
    font-weight: 750;
  }

  strong {
    margin-top: 4px;
    font-size: 15px;
    line-height: 1.2;
  }
}

@media (max-width: 719px) {
  .resident-home {
    width: 100%;
    padding: 12px 14px 100px;
  }

  .hero-panel {
    min-height: 142px;
    padding: 22px 18px;
    border-radius: 20px;

    h1 {
      font-size: 26px;
    }

    p {
      font-size: 14px;
    }

    img {
      width: 46px;
      height: 46px;
      flex-basis: 46px;
      border-radius: 14px;
    }
  }

  .skeleton-hero {
    min-height: 142px;
    padding: 22px 18px;
    border-radius: 20px;

    > div {
      width: min(76%, 360px);
    }
  }

  .skeleton-avatar {
    width: 46px;
    height: 46px;
    flex-basis: 46px;
    border-radius: 14px;
  }

  .home-action-strip {
    grid-auto-columns: minmax(188px, 0.82fr);
    grid-auto-flow: column;
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 2px;
    scroll-snap-type: x proximity;
  }

  .action-card {
    min-height: 82px;
    scroll-snap-align: start;
  }

  .promotion-rail {
    grid-auto-columns: minmax(216px, 82%);
  }

  .quick-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .skeleton-shortcuts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .status-card {
    align-items: flex-start;
  }

  .skeleton-status {
    align-items: flex-start;
  }

  .monthly-statement-card {
    padding: 16px;
  }

  .skeleton-card {
    padding: 16px;
  }

  .statement-header {
    align-items: flex-start;

    > strong {
      font-size: 18px;
    }
  }

  .statement-values {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .skeleton-metric-grid {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .statement-history-chart {
    grid-auto-columns: 74px;
  }
}
</style>
